/**
 * features/account/UserSettings.tsx
 * ------------------------------------------------------------------
 * AUDIT M7 — the account screen (profile + change password). All
 * the logic already existed in AuthContext (changePassword,
 * validatePassword); this is the missing UI surface. No new auth
 * logic is introduced here.
 * ------------------------------------------------------------------
 */
import { useState } from 'react';
import {
  KeyRound,
  Check,
  AlertTriangle,
  User as UserIcon,
  ShieldCheck,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { validatePassword } from '@/contexts/AuthContext';
import { ROLES } from '@/lib/permissions';
import {
  GradientHero,
  SoftCard,
  glassFieldClass,
} from '@/components/ui/glass';

/** `embedded`: inside the pupil portal (ported from Al-Nur), whose pane
 *  already carries the page title, so the hero banner is left off. */
export default function UserSettings({ embedded = false }: { embedded?: boolean } = {}) {
  const { userData, userRole, changePassword } = useAuth();

  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(
    null,
  );

  const roleLabel = userRole ? ROLES[userRole]?.label : '—';
  const v = next ? validatePassword(next) : null;

  async function submit() {
    setMsg(null);
    if (!cur || !next) {
      setMsg({ ok: false, text: 'Fill in all fields.' });
      return;
    }
    if (next !== confirm) {
      setMsg({ ok: false, text: 'New passwords do not match.' });
      return;
    }
    if (v && !v.isValid) {
      setMsg({ ok: false, text: v.errors.join('. ') });
      return;
    }
    setBusy(true);
    try {
      await changePassword(cur, next);
      setMsg({ ok: true, text: 'Password changed successfully.' });
      setCur('');
      setNext('');
      setConfirm('');
    } catch (e) {
      const err = e as Error & { code?: string };
      const friendly =
        err.code === 'auth/invalid-credential' ||
        err.code === 'auth/wrong-password'
          ? 'Your current password is incorrect.'
          : err.message || 'Could not change password.';
      setMsg({ ok: false, text: friendly });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      {!embedded && (
        <GradientHero
          icon={UserIcon}
          title="Account settings"
          subtitle="Your profile and password"
        />
      )}

      <SoftCard pad="p-5">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
          Profile
        </p>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Name</p>
            <p className="font-semibold text-gray-900 dark:text-white">
              {userData?.displayName ?? '—'}
            </p>
          </div>
          <div>
            <p className="text-gray-500">Email</p>
            <p className="font-semibold text-gray-900 dark:text-white">
              {userData?.email ?? '—'}
            </p>
          </div>
          <div>
            <p className="text-gray-500">Role</p>
            <p className="font-semibold text-gray-900 dark:text-white">
              {roleLabel}
            </p>
          </div>
          <div>
            <p className="text-gray-500">Status</p>
            <p className="font-semibold text-emerald-600">
              {userData?.isActive === false ? 'Inactive' : 'Active'}
            </p>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-3">
          Name, email and role are managed by an administrator in
          User Management.
        </p>
      </SoftCard>

      <SoftCard pad="p-5">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound size={16} className="text-primary" />
          <p className="font-bold text-gray-800 dark:text-white">
            Change password
          </p>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          You'll be asked for your current password to confirm it's
          you.
        </p>

        {msg && (
          <div
            className={`text-sm rounded-xl px-4 py-3 border flex items-center gap-2 mb-4 ${
              msg.ok
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
            }`}
          >
            {msg.ok ? (
              <Check size={15} />
            ) : (
              <AlertTriangle size={15} />
            )}
            {msg.text}
          </div>
        )}

        <div className="space-y-3 max-w-md">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
              Current password
            </label>
            <input
              type="password"
              value={cur}
              onChange={(e) => setCur(e.target.value)}
              className={glassFieldClass('mt-1 py-2.5')}
              autoComplete="current-password"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
              New password
            </label>
            <input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className={glassFieldClass('mt-1 py-2.5')}
              autoComplete="new-password"
            />
            {v && (
              <div className="mt-1.5 flex items-center gap-2">
                <div className="flex-1 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      v.score >= 4
                        ? 'bg-emerald-500 w-full'
                        : v.score >= 3
                          ? 'bg-amber-500 w-3/4'
                          : 'bg-red-500 w-1/3'
                    }`}
                  />
                </div>
                <span className="text-[11px] text-gray-500 capitalize">
                  {v.strength}
                </span>
              </div>
            )}
            {v && !v.isValid && (
              <ul className="mt-1.5 space-y-0.5">
                {v.errors.map((er) => (
                  <li
                    key={er}
                    className="text-[11px] text-gray-500 flex items-center gap-1"
                  >
                    <span className="w-1 h-1 rounded-full bg-gray-400" />
                    {er}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
              Confirm new password
            </label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={glassFieldClass('mt-1 py-2.5')}
              autoComplete="new-password"
            />
            {confirm && confirm !== next && (
              <p className="text-[11px] text-red-500 mt-1">
                Passwords don't match.
              </p>
            )}
          </div>
          <button
            onClick={submit}
            disabled={busy}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#2d5295] to-[#1b3a6b] text-white font-semibold text-sm hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:hover:scale-100"
          >
            <ShieldCheck size={16} />
            {busy ? 'Changing…' : 'Change password'}
          </button>
        </div>
      </SoftCard>
    </div>
  );
}
